import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaAngleDown, FaAngleUp } from "react-icons/fa6";
import { MdRefresh } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import TeamList from "../../assets/data/teamList.json";
import {
  selectEmail,
  selectFont,
  selectModel,
  selectRating,
  setIncludeMyTeam,
  setExternalUsers,
} from "../../redux/adminDashboardSlice";
import Button from "../buttons/Button";

const Dropdown = ({ data, title, handleApply }) => {
  const dispatch = useDispatch();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterData, setFilterData] = useState(data);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  const {
    selectedModel,
    selectedRating,
    selectedEmail,
    selectedFont,
    includeMyTeam,
    externalUsers,
  } = useSelector((state) => state.adminDashboard);

  // Toggle selection for individual items and dispatch to Redux
  const handleItemSelection = (email) => {
    setSelectedItems((prevSelectedItems) => {
      const updatedSelectedItems = prevSelectedItems.includes(email)
        ? prevSelectedItems.filter((item) => item !== email)
        : [...prevSelectedItems, email];

      if (title === "Select Model") dispatch(selectModel(updatedSelectedItems));
      else if (title === "Rating") dispatch(selectRating(updatedSelectedItems));
      else if (title === "Email") dispatch(selectEmail(updatedSelectedItems));
      else if (title === "Select Font") dispatch(selectFont(updatedSelectedItems));

      return updatedSelectedItems;
    });
  };

  // Toggle "Select All" feature
  const handleSelectAll = () => {
    const allEmails = data.map((item) => item.email);

    const filteredEmails = allEmails.filter((email) => !TeamList.includes(email));
    const filteredData = data.filter((item) => !TeamList.includes(item.email));

    setSelectedItems((prevSelectedItems) => {
      const isAllSelected = prevSelectedItems.length === filteredData.length;

      const newSelectedItems = isAllSelected ? [] : filteredData;

      if (title === "Select Model") dispatch(selectModel(newSelectedItems));
      else if (title === "Rating") dispatch(selectRating(newSelectedItems));
      else if (title === "Email") dispatch(selectEmail(newSelectedItems.map(item => item.email)));
      else if (title === "Select Font") dispatch(selectFont(newSelectedItems));

      return newSelectedItems;
    });
  };

  // Apply filters using Redux state data
  const handleApplyClick = () => {
    handleApply();
    setIsDropdownOpen(false);
  };

  // Reset local and Redux filter states
  const handleReset = () => {
    if (title === "Select Model") dispatch(selectModel([]));
    else if (title === "Rating") dispatch(selectRating([]));
    else if (title === "Email") dispatch(selectEmail([]));
    else if (title === "Select Font") dispatch(selectFont([]));
  };

  const handleExcludeMyTeam = () => {
    if (includeMyTeam) {
      dispatch(setIncludeMyTeam(false));
      setSelectedItems([]);
      dispatch(selectEmail([]));
    } else {
      dispatch(setIncludeMyTeam(true));

      const teamEmails = data
        .filter(item => TeamList.includes(item.email))
        .map(item => item.email);

      setSelectedItems(teamEmails);
      dispatch(selectEmail(teamEmails));
    }
  };

  const handleSelectExternalUsers = () => {
    
    if (externalUsers) {
      dispatch(setExternalUsers(false));
      setSelectedItems([]);
      dispatch(selectEmail([]));
    } else {
      dispatch(setExternalUsers(true));

      const externalEmails = data
        .filter(item => !item.email.endsWith("@monotype.com") && !TeamList.includes(item.email))
        .map(item => item.email);

      setSelectedItems(externalEmails);
      dispatch(selectEmail(externalEmails));
    }
  };

  // Retain selected items based on title and Redux values
  useEffect(() => {
    if (title === "Select Model") setSelectedItems(selectedModel);
    else if (title === "Rating") setSelectedItems(selectedRating);
    else if (title === "Email") setSelectedItems(selectedEmail);
    else if (title === "Select Font") setSelectedItems(selectedFont);
  }, [title, selectedModel, selectedRating, selectedEmail, selectedFont]);

  // Filter data based on search term and excludeMyTeam
  useEffect(() => {
    if (title === "Email") {
      const filtered = data.filter((item) => {
        const matchesSearch = item.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

        if (includeMyTeam) {
          return matchesSearch && TeamList.includes(item.email);
        } else if (externalUsers) {
          return matchesSearch && !item.email.endsWith("@monotype.com") && !TeamList.includes(item.email);
        } else {
          return matchesSearch && !TeamList.includes(item.email);
        }
      });

      setFilterData(filtered);
    }
  }, [searchTerm, includeMyTeam, externalUsers, data, title]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Render users for the "Email" dropdown
  const renderUsers = useCallback(() => {
    return (
      <div className="overflow-y-auto max-h-[400px] w-[350px] max-w-[350px]">
        {filterData.map((item, i) => {
          const isChecked = selectedEmail.includes(item.email);

          return (
            <div
              key={i}
              className="px-3 mb-1 py-2 rounded cursor-pointer flex items-center gap-2 text-wrap"
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => handleItemSelection(item.email)}
              />
              <div onClick={() => handleItemSelection(item.email)} className="d-flex flex-column">
                <span className="font-weight-bold">{item.name}</span>
                <span className="text-[12px]">{item.email}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [selectedEmail, includeMyTeam, filterData]);

  // Render other dropdowns for "Select Model" and "Rating"
  const renderOtherDropdowns = () => (
    <div className="overflow-auto max-h-[400px] min-w-[250px] max-w-[700px]">
      <div onClick={handleSelectAll} className="px-3 py-2 flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={selectedItems.length === data.length}
          onChange={handleSelectAll}
        />
        Select all
      </div>
      {data.map((item, i) => (
        <div
          key={i}
          className="px-3 mb-1 py-2 rounded cursor-pointer flex items-center gap-2 "
        >
          <input
            type="checkbox"
            checked={selectedItems.includes(item)}
            onChange={() => handleItemSelection(item)}
          />
          <p onClick={() => handleItemSelection(item)}>{item}</p>
        </div>
      ))}
    </div>
  );


  const renderFonts = () => (
    <div className="overflow-auto max-h-[400px] min-w-[250px] max-w-[300px]">
      <div onClick={handleSelectAll} className="w-352 px-3 py-2 flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={selectedItems.length === data.length}
          onChange={handleSelectAll}
        />
        Select all
      </div>
      {data.map((item, i) => (
        <div
          key={i}
          className="px-3 mb-1 py-2 rounded cursor-pointer flex items-center gap-2 text-wrap"
        >
          <input
            type="checkbox"
            checked={selectedItems.includes(item)}
            onChange={() => handleItemSelection(item)}
          />
          <p onClick={() => handleItemSelection(item)}>{item.fontFamily}</p>
        </div>
      ))}
    </div>
  );

  return (
    <div
      ref={dropdownRef}
      className="relative h-100 w-[200px] border-l border-border flex justify-center"
    >
      <button
        onClick={toggleDropdown}
        className={`h-full w-full flex items-center justify-between outline-none px-4 ${selectedItems?.length > 0 && 'border-b border-pink-primary'}`}
      >
        <p className="m-0 ">
          {selectedItems.length === 0
            ? title
            : `${selectedItems.length} Selected`}
        </p>
        {isDropdownOpen ? <FaAngleUp /> : <FaAngleDown />}
      </button>
      {isDropdownOpen && (
        <div className="absolute p-2 z-30 top-14 rounded-lg border bg-white">
          <div className=" pl-3 pr-1 flex items-center justify-between gap-2">
            <span>{title}</span>
            <button
              className="btn btn-sm text-muted d-flex align-items-center gap-2"
              onClick={handleReset}
            >
              <MdRefresh
                size={20}
                className="transform rotate"
                style={{ transform: "scaleX(-1)" }}
              />
              Reset
            </button>
          </div>


          {title === "Email" && (
            <div className="w-352 px-3 py-2 flex  gap-2">
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className=" outline-none "
              />
              <div className="flex  items-center gap-2">
                {/* <div className="px-3 py-2 flex items-center gap-2 cursor-pointer text-[13px]">
                  <input
                    type="checkbox"
                    checked={selectedEmail.length === (data.length - TeamList.length)}
                    onChange={handleSelectAll}
                  />
                  All
                </div> */}
                <div className="flex gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={externalUsers}
                      onChange={handleSelectExternalUsers}
                      className="cursor-pointer"
                    />
                    <p className="text-[13px] cursor-pointer">External</p>
                  </label>
                </div>
                <div className="flex gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeMyTeam}
                      onChange={handleExcludeMyTeam}
                      className="cursor-pointer"
                    />
                    <p className="text-[13px] cursor-pointer">Team</p>
                  </label>
                </div>

              </div>


            </div>
          )}

          {(title === "Select Model" || title === "Rating") &&
            renderOtherDropdowns()}
          {title === "Email" && renderUsers()}
          {title === "Select Font" && renderFonts()}

          <div className="h-76 w-100 border-top border-border p-2 d-flex gap-2 align-items-center">
            <Button onClick={() => setIsDropdownOpen(false)} label='Cancel' className="h-[40px] flexCenter bg-gray-light hover:bg-gray-hoverLight text-cus_primary " />
            <Button onClick={handleApplyClick} label='Apply' className={`h-[40px] flexCenter bg-blue-primary hover:bg-blue-dark text-white `} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
